import React, { ReactElement, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { useGetStoredPermissions, useAuth } from 'src/hooks';

import { createMarkup } from 'src/utils/common';
import { activeStyles } from '../utils/personAlertConstants';
import { classNames } from 'src/utils/className';

import { usePersonAlertStore } from 'src/features/AddEditPersonAlert/store/personAlertStore';

import { PersonAlertListType } from '../types/personAlerts';
import { useSaveChangesParamStore } from 'src/store/useSaveChangesParamStore';

type ALertListType = {
  alertItem: PersonAlertListType;
  handleRowClick: (val: PersonAlertListType) => void;
  selectedAlert: PersonAlertListType | undefined;
  setSelectedAlert: any;
  previewRefetch: any;
  setDocumentPreviewModal: any;
};

function AlertListItem({
  alertItem,
  handleRowClick,
  selectedAlert,
  setSelectedAlert,
  previewRefetch,
  setDocumentPreviewModal,
}: ALertListType): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useGetStoredPermissions();
  const { isExternalUser } = useAuth();

  const setPersonApiCalled = usePersonAlertStore(
    useCallback(state => state?.setPersonApiCalled, []),
  );

  const deleteAlertDetails = usePersonAlertStore(
    useCallback(state => state?.deleteAlertDetails, []),
  );

  const bDate = useMemo(() => {
    const temp =
      alertItem?.Person?.PersonAlertDetails !== null
        ? alertItem?.Person?.PersonAlertDetails?.Birthdate
        : alertItem?.Person?.Birthdate;
    return temp;
  }, [alertItem?.Person?.Birthdate, alertItem?.Person?.PersonAlertDetails]);

  const pName = useMemo(() => {
    const temp =
      alertItem?.Person?.PersonAlertDetails !== null
        ? alertItem?.Person?.PersonAlertDetails?.PreferredName
        : alertItem?.Person?.PreferredName;
    return temp;
  }, [alertItem?.Person?.PersonAlertDetails, alertItem?.Person?.PreferredName]);

  function previewHandler() {
    setSelectedAlert(alertItem);
    if (
      !permissions?.CanSetUpPersonAlerts &&
      permissions?.CanViewPersonAlerts
    ) {
      setTimeout(() => {
        previewRefetch().then(() => {
          setDocumentPreviewModal(true);
        });
      }, 500);
    }
  }

  return (
    <>
      <div
        tabIndex={0}
        className={`relative flex flex-wrap gap-5 rounded bg-white p-2 shadow @container focus-visible:outline  focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-blue-600 dark:bg-neutral-800 dark:focus-visible:outline-blue-400 lg:gap-x-5${
          selectedAlert?.Id === alertItem?.Id ? activeStyles : ''
        }`}
        onClick={() => {
          handleRowClick(alertItem);
        }}
        onDoubleClick={() => {
          useSaveChangesParamStore.getState().setIsDirty(false);
          setPersonApiCalled(false);
          deleteAlertDetails();
          if (
            !permissions?.CanSetUpPersonAlerts &&
            permissions?.CanViewPersonAlerts
          ) {
            queryClient.invalidateQueries(['get_person_alert_preview'], {
              refetchActive: false,
            });

            previewHandler();
            return;
          }
          queryClient.invalidateQueries(['add-edit-person-alert'], {
            refetchActive: false,
          });
          navigate(
            `/PersonAlert/Edit/${alertItem?.Id}?accountId=${alertItem?.Person?.AccountId}`,
          );
        }}
      >
        <div className="order-none flex basis-full gap-5 sm:order-1 md:basis-[30%] md:flex-col lg:order-none lg:basis-[8%]">
          <div className="flex-1 md:flex-initial">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              CID
            </label>
            <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
              {alertItem?.Person?.Account?.CID}
            </p>
          </div>
          <div className="flex-1 md:flex-initial">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              PID
            </label>
            <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
              {alertItem?.Person?.Account?.PID}
            </p>
          </div>
        </div>
        {/* <div className="flex min-w-[70px] basis-[45%] flex-col gap-3 p-2 md:basis-[30%] lg:basis-[7%]">
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              CID
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.Person?.Account?.CID}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              PID
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.Person?.Account?.PID}
            </p>
          </div>
        </div> */}

        <div className="order-none flex basis-full flex-col gap-5 sm:order-2  md:basis-[30%] lg:order-none lg:basis-[10%]">
          <div>
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Alert Number
            </label>
            <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
              {alertItem?.Id}
            </p>
          </div>
        </div>
        {/* <div className="min-w-[92px] basis-[45%] gap-2 p-2 md:basis-[30%] lg:basis-[10%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Alert Number
          </label>
          <p className="mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white">
            {alertItem?.Id}
          </p>
        </div> */}

        {!isExternalUser && (
          <div className="order-none flex basis-full flex-col gap-5 sm:order-4 md:basis-[30%] lg:order-none lg:basis-[10%]">
            <div>
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Person ID
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.PersonId}
              </p>
            </div>
          </div>
        )}

        <div className="order-none flex basis-full flex-col gap-5 sm:order-4 md:basis-[30%] lg:order-none lg:basis-[16%]">
          <div className="flex flex-col gap-5">
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                First Name
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.Person?.FirstName}
              </p>
            </div>
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Last Name
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.Person?.LastName}
              </p>
            </div>
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Alternate Name
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {pName ? pName : ''}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="flex min-w-[70px] basis-[45%] flex-col gap-3 p-2 md:basis-[30%] lg:basis-[18%]">
          <div className="flex flex-col items-baseline gap-1 xl:flex-row">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              First Name
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.Person?.FirstName}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Last Name
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.Person?.LastName}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Alternate Name
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.Person?.PreferredName}
            </p>
          </div>
        </div> */}

        <div className="order-none flex basis-full flex-col gap-5 sm:order-3 md:basis-[30%] lg:order-none lg:basis-[12%]">
          <div>
            <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
              Birth date
            </label>
            <p
              className={classNames(
                'mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white',
                bDate === null ? 'pt-6' : '',
              )}
            >
              {bDate != null ? dayjs(bDate).format('MM/DD/YYYY') : ''}
            </p>
          </div>
        </div>

        {/* <div className="min-w-[92px] basis-[45%] gap-2 p-2 md:basis-[30%] lg:basis-[8%]">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Birth date
          </label>
          <p
            className={classNames(
              'mt-0.5 text-xs font-bold tracking-wide text-black dark:text-white',
              bDate === null ? 'pt-6' : '',
            )}
          >
            {bDate != null ? dayjs(bDate).format('MM/DD/YYYY') : ''}
          </p>
        </div> */}

        <div className="order-none flex basis-full flex-col flex-wrap gap-5 @7xl:!basis-[24%] @7xl:flex-row sm:order-5 md:basis-[30%] lg:order-none lg:basis-[16%]">
          <div className="flex gap-5 @7xl:flex-1 md:flex-col">
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Entered Date
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.EnteredDate != null
                  ? dayjs(alertItem?.EnteredDate).format('MM/DD/YYYY H:mm:ss')
                  : ''}
              </p>
            </div>
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Start Date
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.StartDate != null
                  ? dayjs(alertItem?.StartDate).format('MM/DD/YYYY H:mm:ss')
                  : ''}
              </p>
            </div>
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Expiration Date
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.EndDate != null
                  ? dayjs(alertItem?.EndDate).format('MM/DD/YYYY H:mm:ss')
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex gap-5 @7xl:flex-1 @7xl:ml-4 md:flex-col">
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Updated by
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.UpdatedBy ?? ''}
              </p>
            </div>
            <div className="flex-1 md:flex-initial">
              <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
                Updated date
              </label>
              <p className="prevent-text-breakout break-words text-xs font-bold tracking-wide text-black dark:text-white">
                {alertItem?.UpdatedOn != null
                  ? dayjs(alertItem?.UpdatedOn).format('MM/DD/YYYY H:mm:ss')
                  : ''}
              </p>
            </div>
          </div>
        </div>
        {/* <div className="flex min-w-[70px] basis-[45%] flex-col gap-3 p-2 md:basis-[30%] lg:basis-[20%]">
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Start Date
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.StartDate != null
                ? dayjs(alertItem?.StartDate).format('MM/DD/YYYY H:mm:ss')
                : ''}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Expiration Date
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.EndDate != null
                ? dayjs(alertItem?.EndDate).format('MM/DD/YYYY H:mm:ss')
                : ''}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Entered Date
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.EnteredDate != null
                ? dayjs(alertItem?.EnteredDate).format('MM/DD/YYYY H:mm:ss')
                : ''}
            </p>
          </div>
        </div> */}

        {/* <div className="flex min-w-[70px] basis-[45%] flex-col gap-3 p-2 md:basis-[30%] lg:basis-[20%]">
          <div className="flex flex-col items-baseline gap-1 xl:flex-row">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Updated by
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.UpdatedBy ?? ''}
            </p>
          </div>
          <div className="flex flex-col items-baseline gap-1 xl:flex-row xl:flex-wrap">
            <label className="block min-w-24 whitespace-nowrap text-[10px] font-medium uppercase leading-none tracking-wide opacity-70">
              Updated date
            </label>
            <p className="prevent-text-breakout text-xs font-bold leading-tight tracking-wide text-black dark:text-white">
              {alertItem?.UpdatedOn != null
                ? dayjs(alertItem?.UpdatedOn).format('MM/DD/YYYY H:mm:ss')
                : ''}
            </p>
          </div>
        </div> */}
        <div className="order-none basis-full sm:order-7 lg:order-none">
          <label className="block whitespace-nowrap text-[10px] font-medium uppercase tracking-wide opacity-70">
            Situation
          </label>
          <div
            className="prevent-text-breakout prose max-w-full dark:prose-invert "
            dangerouslySetInnerHTML={createMarkup(
              alertItem?.SituationDescription || '',
            )}
          ></div>
        </div>
      </div>
    </>
  );
}

export { AlertListItem };
